import React, { useEffect, useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box } from '@mui/material';
import { formatFormDateValue, parseFormDateValue } from './formDateUtils';

interface FormikFutureDatePickerProps {
  onChange: (date: string | null) => void;
  label?: string;
  disabled?: boolean;
  initialValue?: string | Date;
  fieldValue?: string | Date | null;
}

export const FormikFutureDatePicker: React.FC<FormikFutureDatePickerProps> = ({
  onChange,
  label = 'Select Date',
  disabled = false,
  initialValue = null,
  fieldValue = null
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    parseFormDateValue(fieldValue ?? initialValue)
  );

  useEffect(() => {
    const parsed = parseFormDateValue(fieldValue ?? initialValue);
    if (!parsed) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate(parsed);
    const formatted = formatFormDateValue(parsed);
    if (!fieldValue && initialValue) {
      onChangeRef.current(formatted);
    }
  }, [fieldValue, initialValue]);

  const handleDateChange = (date: Date | null, context: { validationError?: string }) => {
    if (date && !context.validationError) {
      setSelectedDate(date);
      onChange(formatFormDateValue(date));
    } else if (!date) {
      setSelectedDate(null);
      onChange(null);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Box className="datepicker_field_wrapper">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          disabled={disabled}
          minDate={today}
          disablePast
          slotProps={{
            textField: {
              onKeyDown: (e) => {
                e.preventDefault();
              },
              inputProps: {
                readOnly: true
              },
              fullWidth: true
            }
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default FormikFutureDatePicker;
